import Container from '../../atoms/container/searchVirtualKeyboardContainer';
import Focus from '../../../../shared/types/enums/focus';
import VirtualKeyboard from './virtualKeyboard';
import ConfirmKey from '../../elements/keys/confirmKey';
import DeleteKey from '../../elements/keys/deleteKey';
import SpaceKey from '../../elements/keys/spaceKey';
import Key, { JumpPosition } from '../../../../shared/classes/key';
import VIRTUAL_KEYBOARD_ACTIONS from '../../../../shared/types/enums/virtualKeyboardActions';
import { AutoComplete } from '@shared/types/interfaces/search';
import { letters } from './keyData';
import { useMemo } from 'react';
import NumbersKey from '@components/elements/keys/numbersKey';
import EraseKey from '@components/elements/keys/eraseKey';
import useVirtualKeyboard from '../../../utils/hooks/useVirtualKeyboard';
import { KEYBOARDS } from '../search/searchKeyboard';

const MAX_COLS = 6;

function SearchKeyboard({
  clearAction,
  searchAction,
  onChange,
  onDelete,
  outOfBounds,
  swapKeyboardAction,
}: {
  clearAction: () => void;
  searchAction: (query?: AutoComplete) => void;
  onChange: (char: string) => void;
  onDelete: (pressed?: boolean) => void;
  outOfBounds: (focus: Focus, key: string) => void;
  swapKeyboardAction: React.Dispatch<React.SetStateAction<KEYBOARDS>>;
}) {
  const { getKeys, getGaps } = useVirtualKeyboard(MAX_COLS);
  // Key and gap getters

  // Data initialization

  const keys = useMemo(() => {
    // Create squared gaps and keys
    const gaps = getGaps([letters], false);

    const { keys: letterKeys, stride } = getKeys(letters, gaps, 0, false);

    const charKeys = [...letterKeys, ...gaps].sort(
      (a, b) => (a.spaces as number) - (b.spaces as number)
    );

    // Create irregular-sized keys

    const spaceIndex = [stride, stride + 1];
    const spaceKey = new Key({
      action: VIRTUAL_KEYBOARD_ACTIONS.WRITE,
      component: <SpaceKey char={' '} index={spaceIndex} key={spaceIndex[0]} />,
      jump: new JumpPosition(
        spaceIndex[0] - 1,
        spaceIndex[1] + 1,
        spaceIndex[0] - MAX_COLS,
        spaceIndex[0] + MAX_COLS
      ),
      spaces: spaceIndex,
    });

    const deleteIndex = [stride + 2];
    const deleteKey = new Key({
      action: VIRTUAL_KEYBOARD_ACTIONS.DELETE,
      component: <DeleteKey index={deleteIndex} key={deleteIndex[0]} />,
      jump: new JumpPosition(
        deleteIndex[0] - 1,
        -1,
        deleteIndex[0] - MAX_COLS,
        deleteIndex[0] + MAX_COLS
      ),
      spaces: deleteIndex,
    });

    const numbersIndex = [stride + 3, stride + 4];
    const numbersKey = new Key({
      action: VIRTUAL_KEYBOARD_ACTIONS.CUSTOM,
      component: <NumbersKey index={numbersIndex} key={numbersIndex[0]} />,
      jump: new JumpPosition(
        -1,
        numbersIndex[1] + 1,
        numbersIndex[0] - MAX_COLS,
        -1
      ),
      spaces: numbersIndex,
      customAction: () => swapKeyboardAction(KEYBOARDS.Numbers),
    });

    const clearIndex = [stride + 5];
    const clearKey = new Key({
      action: VIRTUAL_KEYBOARD_ACTIONS.CUSTOM,
      component: <EraseKey index={clearIndex} key={clearIndex[0]} />,
      jump: new JumpPosition(
        clearIndex[0] - 1,
        clearIndex[0] + 1,
        clearIndex[0] - MAX_COLS,
        -1
      ),
      spaces: clearIndex,
      customAction: clearAction,
    });

    const confirmIndex = [stride + 6, stride + 7, stride + 8];
    const confirmKey = new Key({
      action: VIRTUAL_KEYBOARD_ACTIONS.CUSTOM,
      component: <ConfirmKey index={confirmIndex} key={confirmIndex[0]} />,
      jump: new JumpPosition(
        confirmIndex[0] - 1,
        -1,
        confirmIndex[0] - MAX_COLS,
        -1
      ),
      spaces: confirmIndex,
      customAction: searchAction,
    });

    // Merge and sort gaps and keys
    return [...charKeys, spaceKey, deleteKey, numbersKey, clearKey, confirmKey];
  }, []);

  return (
    <Container>
      <VirtualKeyboard
        focus={Focus.SEARCH_KEYBOARD}
        keys={keys}
        maxCols={MAX_COLS}
        onChange={onChange}
        onDelete={onDelete}
        outOfBounds={outOfBounds}
      />
    </Container>
  );
}

export default SearchKeyboard;
